// Pure logic for the manual questionnaire entry surface: section statistics
// and the question→section index. Extracted from the component so completion
// and saved-state semantics are unit-testable without a renderer — the
// "section shows green but nothing persisted" class of bug is exactly what
// escapes UI-only verification.

import {
  answerIsPresent,
  evaluateQuestionnaireV1,
  normalizeUtteranceV1,
  parseAnswerNormalizationV1,
  parseYesNoUtteranceV1,
  resolveClinicianQuestionnaireReviewPromptText,
  type ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  type TenantAdminOutboundQuestionnaireQuestionV1,
} from "@amplecare/contracts";

export type QuestionnaireSectionState =
  | "complete"
  | "in-progress"
  | "attention"
  | "untouched";

export interface QuestionnaireSectionStat {
  answered: number;
  total: number;
  state: QuestionnaireSectionState;
  redFlag: boolean;
  /** Persisted to the record — distinct from "all questions answered". */
  saved: boolean;
}

/** Why a single answer could not be persisted. The save handler used to keep
 *  only a count, so the surface could say "2 answers need attention" and had
 *  nothing to point at — the identities were computed and discarded on the
 *  same line that counted them. */
export type QuestionnaireSaveIssueReason =
  /** The stored value failed validateRecordValue. */
  | "invalid-value"
  /** Answered, but no binding maps it to a record field. */
  | "no-binding"
  /** The transform could not interpret the answer ("about six feet"). */
  | "unreadable-answer"
  /** Blanked, but the record field was not written by the questionnaire. */
  | "clear-refused";

export interface QuestionnaireSaveIssue {
  questionKey: string;
  reason: QuestionnaireSaveIssueReason;
  /** True when the issue arrived via a field path bound by several questions,
   *  so the surface can say "one of these" instead of naming a single culprit
   *  it cannot actually identify. */
  sharedFieldPath?: boolean;
  /** The answer as it stood when the save failed, JSON-encoded. The marker is
   *  hidden once the current answer differs, so a question the nurse has since
   *  corrected stops reading as still-broken — without clearing the whole
   *  section, which would hide the issues they have not fixed yet. */
  answerSnapshot: string;
}

export function encodeQuestionnaireAnswerSnapshot(value: unknown): string {
  return JSON.stringify(value ?? null);
}

export interface SectionStatsInput {
  sections: {
    key: string;
    questions: {
      questionKey: string;
      redFlagRules?: unknown;
    }[];
  }[];
  visibleKeys: Set<string>;
  answers: Record<string, unknown>;
  savedSections: Set<string>;
  sectionIssues: Record<string, readonly QuestionnaireSaveIssue[]>;
}

export interface QuestionnairePromptRecordContext {
  case: {
    dateOfService: string | null;
    procedures: string[];
    surgeonName: string | null;
  };
}

export function canonicalizeQuestionnaireUiAnswers(input: {
  answers: Record<string, unknown>;
  questions: TenantAdminOutboundQuestionnaireQuestionV1[];
  unavailableQuestionKeys?: ReadonlySet<string>;
}): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const question of input.questions) {
    if (input.unavailableQuestionKeys?.has(question.questionKey)) {
      continue;
    }

    const raw = input.answers[question.questionKey];

    if (raw === undefined) {
      continue;
    }

    if (
      (question.answerType === "single_choice" ||
        question.answerType === "multi_choice") &&
      Array.isArray(raw)
    ) {
      const values = raw
        .map((entry) =>
          entry != null &&
          typeof entry === "object" &&
          "value" in entry
            ? (entry as { value?: unknown }).value
            : entry,
        )
        .filter((value) => value !== undefined);
      result[question.questionKey] =
        question.answerType === "single_choice" ? values[0] : values;
    } else {
      result[question.questionKey] = raw;
    }
  }

  return result;
}

function firstPromptValueLine(value: string | null | undefined): string | null {
  const firstLine = value
    ?.split(/\r?\n/u)
    .map((line) => line.trim())
    .find((line) => line.length > 0);

  if (!firstLine) {
    return null;
  }

  return firstLine.replace(/^(?:planned\s+)?procedure:\s*/iu, "").trim();
}

function formatPromptDate(value: string | null): string | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value) : null;

  if (!match) {
    return firstPromptValueLine(value);
  }

  return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
}

function promptTokenLabel(token: string): string {
  return token
    .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .replace(/[_-]+/gu, " ")
    .trim()
    .toLowerCase();
}

function formatPromptTokenLabels(tokens: string[]): string {
  const labels = [
    ...new Set(tokens.map(promptTokenLabel).filter((label) => label.length > 0)),
  ];

  if (labels.length === 0) {
    return "information";
  }

  if (labels.length === 1) {
    return labels[0] ?? "information";
  }

  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

const unavailableQuestionnairePromptByToken: Record<string, string> = {
  plannedProcedure:
    "Planned procedure unavailable in the current patient record.",
  scheduledProcedureDate:
    "Scheduled procedure date unavailable in the current patient record.",
  surgeonName: "Surgeon unavailable in the current patient record.",
};

function resolveQuestionnairePromptReplacements(
  record: QuestionnairePromptRecordContext | null | undefined,
): Record<string, string | null> {
  return {
    plannedProcedure:
      record?.case.procedures.map(firstPromptValueLine).find(Boolean) ?? null,
    scheduledProcedureDate: formatPromptDate(
      record?.case.dateOfService ?? null,
    ),
    surgeonName: firstPromptValueLine(record?.case.surgeonName),
  };
}

function collectQuestionnairePromptTokens(promptText: string): string[] {
  return Array.from(
    promptText.matchAll(/\{([A-Za-z][A-Za-z0-9_-]*)\}/gu),
    (match) => match[1],
  ).filter((token): token is string => token != null);
}

export function hasUnresolvedQuestionnairePromptTokens(
  promptText: string,
  record: QuestionnairePromptRecordContext | null | undefined,
): boolean {
  const replacements = resolveQuestionnairePromptReplacements(record);

  return collectQuestionnairePromptTokens(promptText).some(
    (token) => !replacements[token]?.trim(),
  );
}

export function shouldBlockUnresolvedQuestionnairePrompt(input: {
  hasCapturedOutboundAnswer: boolean;
  promptText: string;
  record: QuestionnairePromptRecordContext | null | undefined;
}): boolean {
  return (
    !input.hasCapturedOutboundAnswer &&
    hasUnresolvedQuestionnairePromptTokens(input.promptText, input.record)
  );
}

export function resolveQuestionnairePromptText(
  promptText: string,
  record: QuestionnairePromptRecordContext | null | undefined,
): string {
  const replacements = resolveQuestionnairePromptReplacements(record);
  const tokens = collectQuestionnairePromptTokens(promptText);
  const unresolvedTokens = tokens.filter(
    (token) => !replacements[token]?.trim(),
  );

  if (unresolvedTokens.length > 0) {
    if (tokens.length === 1) {
      const unavailablePrompt =
        unavailableQuestionnairePromptByToken[tokens[0] ?? ""];

      if (unavailablePrompt) {
        return unavailablePrompt;
      }
    }

    return `Required prompt data unavailable: ${formatPromptTokenLabels(
      unresolvedTokens,
    )}.`;
  }

  return promptText.replace(/\{([A-Za-z][A-Za-z0-9_-]*)\}/gu, (_, token) => {
    const replacement = replacements[token];

    return replacement?.trim() ?? "";
  });
}

export function resolveQuestionnaireReviewPromptText(
  questionKey: string,
  promptText: string,
): string {
  return resolveClinicianQuestionnaireReviewPromptText(
    questionKey,
    promptText,
  );
}

interface RedFlagRulesShape {
  reviewSignal?: string;
  yesRequiresReview?: boolean;
  noRequiresReview?: boolean;
  alwaysRequiresReview?: boolean;
  trigger?: string;
}

export type AnswerReviewSignal = "red_flag" | "yellow_flag";

/** Direction-aware review-flag check. The template declares which answer is
 *  alarming per question, checked in strictly this order:
 *  1. alwaysRequiresReview — every present answer flags. A value-conditional
 *     trigger never suppresses this (an unlisted "anaphylactic shock" or an
 *     "unknown" reaction must not lose its flag to an exact-match miss).
 *  2. yesRequiresReview / noRequiresReview — boolean direction fields.
 *  3. A parseable "key=value" trigger on non-boolean answers — only the
 *     named option flags.
 *  4. Otherwise null: rules with no direction knowledge (unsure-only rules,
 *     prose triggers on booleans) must not guess — the old any-non-false
 *     fallback inverted "have you told your surgeon?" and painted flags on
 *     every possible answer of unsure-only questions.
 *  Callers pass canonical values (booleans for yes/no, string/string[] for
 *  choice), not display strings. */
export function answerReviewSignal(
  rules: unknown,
  value: unknown,
): AnswerReviewSignal | null {
  const shape = (rules ?? null) as RedFlagRulesShape | null;
  if (shape == null) return null;
  const signal = shape.reviewSignal;
  if (signal !== "red_flag" && signal !== "yellow_flag") return null;
  if (!answerIsPresent(value)) return null;
  if (shape.alwaysRequiresReview === true) return signal;
  if (shape.yesRequiresReview === true || shape.noRequiresReview === true) {
    return (shape.yesRequiresReview === true && value === true) ||
      (shape.noRequiresReview === true && value === false)
      ? signal
      : null;
  }
  const triggerMatch =
    typeof shape.trigger === "string" && typeof value !== "boolean"
      ? /^[a-z0-9_ ]+=(.+)$/i.exec(shape.trigger)
      : null;
  if (triggerMatch) {
    const flagValue = triggerMatch[1]!.trim().toLowerCase();
    const values = Array.isArray(value) ? value : [value];
    return values.some((entry) => String(entry).trim().toLowerCase() === flagValue)
      ? signal
      : null;
  }
  return null;
}

/** High-alert (red) only — drives the section rail dot and the printed
 *  chart-pack highlight; yellow flags surface per-question, not here. */
/** True when this question's option list was authored by us rather than
 *  observed on VIP's own system, so a clinician can tell the two apart.
 *
 *  Only choice questions can carry the claim — it is about a list of choices,
 *  and free text has no list. Read from `questionMetadata.optionsProvenance`,
 *  which the generator derives from the ledger's `options_visibility`: only a
 *  dropdown watched open in full counts as observed.
 *
 *  Deliberately NOT the row-level `provenance` tag. That is coarse — a question
 *  can be tagged VERBATIM while its voice prompt is PROVISIONAL — so using it
 *  would mark questions for reasons unrelated to their options, and a marker
 *  that fires on things it does not mean gets ignored.
 *
 *  This is not a clinical signal. It says nothing about the patient or their
 *  answer, so it must never be rendered in the review-flag or save-failure
 *  visual language. */
export function questionnaireOptionsAreUnconfirmed(question: unknown): boolean {
  const shape = (question ?? null) as {
    answerType?: string;
    questionMetadata?: { optionsProvenance?: string } | null;
  } | null;
  if (shape == null) return false;
  if (
    shape.answerType !== "single_choice" &&
    shape.answerType !== "multi_choice"
  ) {
    return false;
  }
  return shape.questionMetadata?.optionsProvenance === "authored";
}

export function isRedFlagAnswer(rules: unknown, value: unknown): boolean {
  return answerReviewSignal(rules, value) === "red_flag";
}

export function computeQuestionnaireSectionStats(
  input: SectionStatsInput,
): Map<string, QuestionnaireSectionStat> {
  const { sections, visibleKeys, answers, savedSections, sectionIssues } = input;
  const stats = new Map<string, QuestionnaireSectionStat>();

  for (const section of sections) {
    const visibleQuestions = section.questions.filter((question) =>
      visibleKeys.has(question.questionKey),
    );
    // Presence must match the runtime's rule: an empty string is NOT an
    // answer, or a tabbed-through section turns green while writing nothing.
    const answered = visibleQuestions.filter((question) =>
      answerIsPresent(answers[question.questionKey]),
    ).length;
    const issues = sectionIssues[section.key]?.length ?? 0;
    const redFlag = visibleQuestions.some((question) =>
      isRedFlagAnswer(question.redFlagRules, answers[question.questionKey]),
    );

    const state: QuestionnaireSectionState =
      issues > 0
        ? "attention"
        : visibleQuestions.length > 0 && answered === visibleQuestions.length
          ? "complete"
          : answered > 0
            ? "in-progress"
            : "untouched";

    stats.set(section.key, {
      answered,
      total: visibleQuestions.length,
      state,
      redFlag,
      saved: savedSections.has(section.key),
    });
  }

  return stats;
}

/** Sections whose answers are complete AND persisted — the only honest basis
 *  for the header's "N sections complete" count. */
export function countSavedCompleteSections(
  stats: Map<string, QuestionnaireSectionStat>,
): number {
  return [...stats.values()].filter((stat) => stat.state === "complete" && stat.saved)
    .length;
}

/** Sections holding answers that have not been persisted. */
export function countUnsavedSections(
  stats: Map<string, QuestionnaireSectionStat>,
): number {
  return [...stats.values()].filter((stat) => stat.answered > 0 && !stat.saved).length;
}

export function areQuestionnaireSectionAnswerSnapshotsEqual(
  questionKeys: readonly string[],
  expected: Record<string, unknown>,
  current: Record<string, unknown>,
): boolean {
  return questionKeys.every((questionKey) => {
    const expectedValue = expected[questionKey];
    const currentValue = current[questionKey];

    return (
      Object.is(expectedValue, currentValue) ||
      JSON.stringify(expectedValue) === JSON.stringify(currentValue)
    );
  });
}

export function buildQuestionnaireDirtySectionKeys(input: {
  committedAnswers: Record<string, unknown>;
  effectiveAnswers: Record<string, unknown>;
  questionSectionIndex: ReadonlyMap<string, string>;
  transientDirtyQuestionKeys?: ReadonlySet<string>;
  /**
   * Currently-visible question keys. committedAnswers is seeded from the record
   * for every bound question, while effectiveAnswers is filtered to visible
   * ones — so without this, a stored answer for a question that skip logic
   * hides compares against `undefined` and reads as an unsaved change. Hidden
   * answers are never submitted (the orphan rule), so they cannot be dirty.
   * Optional: callers that already pass matching sets are unaffected.
   */
  visibleQuestionKeys?: ReadonlySet<string>;
}): Set<string> {
  const dirtySectionKeys = new Set<string>();
  const answerKeys = new Set([
    ...Object.keys(input.committedAnswers),
    ...Object.keys(input.effectiveAnswers),
  ]);

  for (const questionKey of answerKeys) {
    if (
      input.visibleQuestionKeys != null &&
      !input.visibleQuestionKeys.has(questionKey)
    ) {
      continue;
    }

    const sectionKey = input.questionSectionIndex.get(questionKey);

    if (
      sectionKey &&
      !areQuestionnaireSectionAnswerSnapshotsEqual(
        [questionKey],
        input.committedAnswers,
        input.effectiveAnswers,
      )
    ) {
      dirtySectionKeys.add(sectionKey);
    }
  }

  for (const questionKey of input.transientDirtyQuestionKeys ?? []) {
    const sectionKey = input.questionSectionIndex.get(questionKey);

    if (sectionKey) {
      dirtySectionKeys.add(sectionKey);
    }
  }

  return dirtySectionKeys;
}

export type QuestionnaireRecordState = "not-started" | "in-progress" | "complete";

export interface QuestionnaireRecordStatus {
  answeredCount: number;
  /** Skip-logic-aware denominator — matches the entry surface's own count. */
  visibleCount: number;
  state: QuestionnaireRecordState;
  /** Provenance of the most recent questionnaire-attributable write. */
  latestSource: string | null;
  latestUpdatedAt: string | null;
}

type EvaluatableQuestions = Parameters<typeof evaluateQuestionnaireV1>[0];

/** Questionnaire coverage derived from the record alone — the entry surface's
 *  live session state is gone once it closes, so the status card must read
 *  what actually persisted. Answers are reconstructed from bound record
 *  fields and run through the same skip-logic evaluator the entry surface
 *  uses, so the denominator excludes demographically impossible questions
 *  and gated-off follow-ups and "complete" is actually reachable.
 *
 *  "Started" requires questionnaire evidence — a clinical.questionnaireAnswers
 *  entry or a field whose provenance is `questionnaire` — so profile edits and
 *  HST/Passport imports that happen to fill bound fields (height, contacts)
 *  do not flip a never-run questionnaire to "in progress". The provenance
 *  line likewise only reflects questionnaire-attributable writes. */
/** Read the answers already stored on the patient record back out through the
 *  binding table. A questionnaire finished by an AI call lives in the record,
 *  not in an open outbound session, so without this the manual-entry form
 *  opens blank on a patient whose Details tab reports the questionnaire
 *  complete — and re-saving it would wipe answers already on file. */
export function buildRecordQuestionnaireAnswers(input: {
  questions: EvaluatableQuestions;
  bindings: { questionKey: string; fieldPath: string }[];
  getFieldValue: (fieldPath: string) => unknown;
}): Record<string, unknown> {
  const questionKeys = new Set(
    input.questions.map((question) => question.questionKey),
  );
  const answers: Record<string, unknown> = {};

  for (const binding of input.bindings) {
    if (!questionKeys.has(binding.questionKey)) {
      continue;
    }

    if (answers[binding.questionKey] !== undefined) {
      continue;
    }

    const value = input.getFieldValue(binding.fieldPath);

    if (answerIsPresent(value)) {
      answers[binding.questionKey] = value;
    }
  }

  return answers;
}

export function computeQuestionnaireRecordStatus(input: {
  questions: EvaluatableQuestions;
  bindings: { questionKey: string; fieldPath: string }[];
  getFieldValue: (fieldPath: string) => unknown;
  resolveMeta: (
    fieldPath: string,
  ) => { source: string; updatedAt: string } | null;
  context: { ageYears?: number | null; sexAtBirth?: string | null };
}): QuestionnaireRecordStatus {
  const questionKeys = new Set(input.questions.map((question) => question.questionKey));
  const pathsByQuestion = new Map<string, string[]>();
  for (const binding of input.bindings) {
    if (!questionKeys.has(binding.questionKey)) continue;
    const paths = pathsByQuestion.get(binding.questionKey) ?? [];
    paths.push(binding.fieldPath);
    pathsByQuestion.set(binding.questionKey, paths);
  }

  const answers: Record<string, unknown> = {};
  let started = false;
  let latestSource: string | null = null;
  let latestUpdatedAt: string | null = null;
  for (const [questionKey, paths] of pathsByQuestion) {
    for (const path of paths) {
      const value = input.getFieldValue(path);
      if (answers[questionKey] === undefined && answerIsPresent(value)) {
        answers[questionKey] = value;
      }
      const isQuestionnaireOwnedPath = path.startsWith(
        "clinical.questionnaireAnswers.",
      );
      const meta = input.resolveMeta(path);
      const isQuestionnaireEvidence =
        meta?.source === "questionnaire" ||
        (isQuestionnaireOwnedPath && answerIsPresent(value));
      if (!isQuestionnaireEvidence) continue;
      started = true;
      // ISO-8601 timestamps compare correctly as strings.
      if (meta && (latestUpdatedAt === null || meta.updatedAt > latestUpdatedAt)) {
        latestUpdatedAt = meta.updatedAt;
        latestSource = meta.source;
      }
    }
  }

  const evaluation = evaluateQuestionnaireV1(input.questions, answers, input.context);
  const answeredCount = evaluation.answeredVisibleCount;
  const visibleCount = evaluation.visibleCount;
  const state: QuestionnaireRecordState = !started
    ? "not-started"
    : visibleCount > 0 && answeredCount === visibleCount
      ? "complete"
      : "in-progress";

  return { answeredCount, visibleCount, state, latestSource, latestUpdatedAt };
}

export interface OutboundPrefillProvenance {
  answeredAt: string | null;
  answerSummary: string;
}

export interface OutboundPrefillResult {
  /** UI-shaped answers keyed by question key (booleans, select wrappers,
   *  strings) — ready to seed the entry surface's answer state. */
  uiAnswers: Record<string, unknown>;
  /** Which keys came from the phone call, with when and what was captured. */
  provenance: Record<string, OutboundPrefillProvenance>;
}

type PrefillPreview = Pick<
  ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  "questionKey" | "answerStatus" | "answerSummary" | "rawAnswerText" | "answeredAt"
>;

/**
 * Maps an interrupted AI call's answer previews into the manual-entry
 * surface's answer shapes, so the clinician continues where the patient left
 * off instead of starting over (GP workflow). Conservative by design: an
 * utterance that does not map cleanly (an "unsure", a failed parse, a
 * medication-loop capture) is left blank for the clinician rather than
 * guessed — the provenance chip still shows what the patient said.
 */
export function buildOutboundPrefillAnswers(input: {
  questions: TenantAdminOutboundQuestionnaireQuestionV1[];
  previews: PrefillPreview[];
}): OutboundPrefillResult {
  const questionByKey = new Map(
    input.questions.map((question) => [question.questionKey, question]),
  );
  const uiAnswers: Record<string, unknown> = {};
  const provenance: Record<string, OutboundPrefillProvenance> = {};

  for (const preview of input.previews) {
    if (preview.answerStatus !== "answered" && preview.answerStatus !== "changed") {
      continue;
    }

    const question = questionByKey.get(preview.questionKey);

    if (!question) continue;

    // Medication-loop captures are structured entries reviewed in the
    // medications panel — free-text prefill would garble them.
    const reconciliation = question.questionMetadata?.medicationReconciliation;
    if (
      reconciliation != null &&
      typeof reconciliation === "object" &&
      (reconciliation as Record<string, unknown>)
        .capturesPatientReportedMedication === true
    ) {
      continue;
    }

    const text = (preview.rawAnswerText ?? preview.answerSummary ?? "").trim();

    if (!text) continue;

    const mapped = mapPreviewToUiAnswer(question, text);

    if (mapped !== undefined) {
      uiAnswers[question.questionKey] = mapped;
      provenance[question.questionKey] = {
        answeredAt: preview.answeredAt ?? null,
        answerSummary: preview.answerSummary || text,
      };
    }
  }

  return { uiAnswers, provenance };
}

function mapPreviewToUiAnswer(
  question: TenantAdminOutboundQuestionnaireQuestionV1,
  text: string,
): unknown {
  switch (question.answerType) {
    case "yes_no":
    case "confirmation": {
      if (text === "true") return true;
      if (text === "false") return false;
      const parsed = parseYesNoUtteranceV1(text);
      return parsed === null ? undefined : parsed;
    }
    case "single_choice":
    case "multi_choice": {
      const normalization = parseAnswerNormalizationV1(
        question.answerNormalization ?? {},
      );
      const rawValues = parseChoiceTexts(text);
      const wrappers = rawValues.map((value) => {
        if (normalization.options.length === 0) {
          return { unlisted: true, value };
        }
        const normalized = normalizeUtteranceV1(value, normalization);
        return {
          unlisted: normalized.match === "unlisted",
          value: normalized.value,
        };
      });
      if (wrappers.length === 0) return undefined;
      return question.answerType === "single_choice"
        ? [wrappers[0]]
        : wrappers;
    }
    case "number": {
      const numeric = Number(text);
      return Number.isFinite(numeric) ? String(numeric) : text;
    }
    default:
      // date / short_text render from strings.
      return text;
  }
}

/** Multi-choice raw answers persist as JSON arrays; single answers as text. */
function parseChoiceTexts(text: string): string[] {
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry) => String(entry).trim())
          .filter((entry) => entry.length > 0);
      }
    } catch {
      // Fall through to the single-text path.
    }
  }

  return text.length > 0 ? [text] : [];
}

export function buildQuestionSectionIndex(
  sections: { key: string; questions: { questionKey: string }[] }[],
): Map<string, string> {
  const index = new Map<string, string>();
  for (const section of sections) {
    for (const question of section.questions) {
      index.set(question.questionKey, section.key);
    }
  }
  return index;
}

export interface ManualCompletionState {
  canComplete: boolean;
  /** Why completion is blocked, when the nurse can act on it. Null when
   *  completion is allowed, or when the block is transient (a command already
   *  in flight) and there is nothing for them to fix. */
  blockText: string | null;
}

/**
 * Whether the clinician may settle this call round manually, and why not.
 *
 * Completing stops automated calling for the round, so it is gated harder than
 * saving a section — which only mirrors answers and leaves the round running.
 *
 * isLiveCallSession is derived from outboundSession.sessionStatus, which is a
 * closed set — calling, identity_verification, in_progress, paused, verified —
 * and never null: outboundSession only exists when the status is one of those
 * (see resolveClinicianOutboundSessionContext). So this gate is total over its
 * input, and unlike the profile's entry lock it has no unknown-status case to
 * decide a fail direction for. If that invariant ever changes, decide the fail
 * direction deliberately: completing stops the call round and cannot be undone
 * from the UI, so it should fail closed.
 */
export function resolveManualCompletionState(input: {
  isLiveCallSession: boolean;
  dirtyCount: number;
  unsavedSectionCount: number;
  validationIssueCount: number;
  requiredMissingCount: number;
  isOutboundCommandSubmitting?: boolean;
}): ManualCompletionState {
  const blockText = input.isLiveCallSession
    ? "The AI call is still in progress. Completing is available once the call ends."
    : input.dirtyCount > 0 || input.unsavedSectionCount > 0
      ? "Save every section containing answers before completing this call round."
      : input.validationIssueCount > 0
        ? `Resolve ${String(input.validationIssueCount)} validation ${
            input.validationIssueCount === 1 ? "issue" : "issues"
          } before completing this call round.`
        : input.requiredMissingCount > 0
          ? `Answer ${String(input.requiredMissingCount)} required ${
              input.requiredMissingCount === 1 ? "question" : "questions"
            } before completing this call round.`
          : null;

  return {
    canComplete:
      !input.isLiveCallSession &&
      input.dirtyCount === 0 &&
      input.unsavedSectionCount === 0 &&
      input.validationIssueCount === 0 &&
      input.requiredMissingCount === 0 &&
      input.isOutboundCommandSubmitting !== true,
    blockText,
  };
}
